/**
 * Adventure Grid — Daily Image + Mobile Slider
 *
 * • Shows one image based on the current day, changes every X days.
 * • On mobile: prev/next arrow buttons scroll the horizontal track.
 * • Zero external dependencies.
 */

class AdventureGrid {
  #intervalDays;
  #container;

  constructor(sectionId) {
    const config = window.adventureGridConfig?.[sectionId] || {};
    this.#intervalDays = config.intervalDays || 1;

    this.#container = document.getElementById(`AdventureGrid-${sectionId}`);
    if (!this.#container) return;

    this.#initDailyImages();
    this.#initSliderArrows();
    this.#initOverlayHovers();
  }

  /* ------------------------------------------------------------------ */
  /* Daily image                                                        */
  /* ------------------------------------------------------------------ */

  #initDailyImages() {
    const cards = this.#container.querySelectorAll('.adventure-grid__card');
    const now = new Date();
    const daysSinceEpoch = Math.floor((now.getTime() - now.getTimezoneOffset() * 60000) / 86400000);

    for (const card of cards) {
      const slideCount = parseInt(card.dataset.slideshow, 10);
      if (slideCount <= 1) continue;

      const slides = card.querySelectorAll('.adventure-grid__slide');
      const activeIndex = Math.floor(daysSinceEpoch / Math.max(1, this.#intervalDays)) % slideCount;

      for (let i = 0; i < slides.length; i++) {
        if (i === activeIndex) {
          slides[i].classList.add('adventure-grid__slide--active');
        } else {
          slides[i].classList.remove('adventure-grid__slide--active');
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Mobile slider arrows                                               */
  /* ------------------------------------------------------------------ */

  #initSliderArrows() {
    const prevBtn = this.#container.querySelector('.adventure-grid__arrow--prev');
    const nextBtn = this.#container.querySelector('.adventure-grid__arrow--next');
    const track = this.#container.querySelector('.adventure-grid__track');

    if (!track || !prevBtn || !nextBtn) return;

    const items = Array.from(track.querySelectorAll('.adventure-grid__item'));

    if (items.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      return;
    }

    let currentIndex = 0;

    const updateArrows = () => {
      prevBtn.style.display = currentIndex <= 0 ? 'none' : '';
      nextBtn.style.display = currentIndex >= items.length - 1 ? 'none' : '';
    };

    const scrollToIndex = (index) => {
      const target = items[index];
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      currentIndex = index;
      updateArrows();
    };

    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) {
        scrollToIndex(currentIndex - 1);
      }
    });

    nextBtn.addEventListener('click', () => {
      if (currentIndex < items.length - 1) {
        scrollToIndex(currentIndex + 1);
      }
    });

    /* Update currentIndex on manual swipe using IntersectionObserver */
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = items.indexOf(entry.target);

            if (idx !== -1) {
              currentIndex = idx;
              updateArrows();
            }
          }
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const item of items) {
      observer.observe(item);
    }

    updateArrows();
  }

  /* ------------------------------------------------------------------ */
  /* Sync hover state between image overlays and text links             */
  /* ------------------------------------------------------------------ */

  #initOverlayHovers() {
    const items = this.#container.querySelectorAll('.adventure-grid__item');

    for (const item of items) {
      const leftOverlay = item.querySelector('.adventure-grid__card-link-half--left');
      const rightOverlay = item.querySelector('.adventure-grid__card-link-half--right');
      const fullOverlay = item.querySelector('.adventure-grid__card-link-half--full');

      const link1 = item.querySelector('.adventure-grid__link--1');
      const link2 = item.querySelector('.adventure-grid__link--2');

      if (leftOverlay && link1) {
        leftOverlay.addEventListener('mouseenter', () => {
          link1.classList.add('is-hovered');
        });
        leftOverlay.addEventListener('mouseleave', () => {
          link1.classList.remove('is-hovered');
        });
      }

      if (rightOverlay && link2) {
        rightOverlay.addEventListener('mouseenter', () => {
          link2.classList.add('is-hovered');
        });
        rightOverlay.addEventListener('mouseleave', () => {
          link2.classList.remove('is-hovered');
        });
      }

      if (fullOverlay && !fullOverlay.classList.contains('adventure-grid__card-link-half--global')) {
        fullOverlay.addEventListener('mouseenter', () => {
          if (link1) link1.classList.add('is-hovered');
          if (link2) link2.classList.add('is-hovered');
        });
        fullOverlay.addEventListener('mouseleave', () => {
          if (link1) link1.classList.remove('is-hovered');
          if (link2) link2.classList.remove('is-hovered');
        });
      }
    }
  }
}

/* Initialise all adventure grids */
const initAdventureGrids = () => {
  const grids = document.querySelectorAll('[id^="AdventureGrid-"]');

  for (const grid of grids) {
    const sectionId = grid.dataset.sectionId;

    if (sectionId) {
      new AdventureGrid(sectionId);
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAdventureGrids);
} else {
  initAdventureGrids();
}

/* Shopify Theme Editor support */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const grid = event.target.querySelector('[id^="AdventureGrid-"]');

    if (grid) {
      new AdventureGrid(grid.dataset.sectionId);
    }
  });
}
